import { DropdownBasic, TextInput } from '@designsforhealth/dfh-react-components'
import React, { useMemo } from 'react'
import { UseFormMethods } from 'react-hook-form/dist/types/form'
import { langCodes } from '../../../lib/config/lang'
import { useCountries } from '../../../lib/countries'
import { SelectFieldOption } from '../../../lib/form'
import { useSiteSettings } from '../../../lib/sanity/hooks/useSiteSettings'
import * as Styled from './styled'
import { ADDRESS_MAX_LENGTH, addressMaxLengthRule } from '../../../lib/form/fieldLengthRules'

interface RegFormAddressValues {
  street1: string
  street2?: string
  city: string
  state: { label: string; value: string }
  zipCode: string
  country: { label: string; value: string }
}

const countryOptions = {
  US: {
    value: 'US',
    label: 'United States',
  },
  CA: {
    value: 'CA',
    label: 'Canada',
  },
  AU: {
    value: 'AU',
    label: 'Australia',
  },
}

const countryOptionsFor = (
  lang: string | undefined | null
): { options: SelectFieldOption[]; defaultOption: SelectFieldOption } => {
  switch (lang) {
    case langCodes.en_US:
      return { options: [countryOptions.US], defaultOption: countryOptions.US }
    case langCodes.en_CA:
      return { options: [countryOptions.CA], defaultOption: countryOptions.CA }
    case langCodes.en_AU:
      return { options: [countryOptions.AU], defaultOption: countryOptions.AU }
    default:
      return { options: [countryOptions.US], defaultOption: countryOptions.US }
  }
}

const determineFieldLabels = (
  lang: string | undefined | null
): { zipCode: string; state: string } => {
  switch (lang) {
    case langCodes.en_US:
      return {
        zipCode: 'Zip Code',
        state: 'State',
      }
    case langCodes.en_CA:
      return {
        zipCode: 'Postal Code',
        state: 'Province',
      }
    default:
      return {
        zipCode: 'Postal Code',
        state: 'Region',
      }
  }
}

export interface RegistrationFormAddressProps {
  disabled: boolean
  register: UseFormMethods<Partial<RegFormAddressValues>>['register']
  errors: UseFormMethods<Partial<RegFormAddressValues>>['errors']
  watch: UseFormMethods<Partial<RegFormAddressValues>>['watch']
  control: UseFormMethods<Partial<RegFormAddressValues>>['control']
}

export const RegistrationFormAddress: React.VFC<RegistrationFormAddressProps> = ({
  disabled,
  register,
  errors,
  watch,
  control,
}) => {
  const { countries } = useCountries()
  const { lang } = useSiteSettings() ?? {}
  const { options: countriesList, defaultOption: defaultCountryOption } = useMemo(
    () => countryOptionsFor(lang),
    [lang]
  )

  const currentCountry = watch('country', defaultCountryOption)
  const fieldLabels = useMemo(() => determineFieldLabels(lang), [lang])

  const regionsList = useMemo<SelectFieldOption[]>(() => {
    const country = countries.find(
      ({ iso }) => currentCountry && currentCountry.value && currentCountry.value === iso
    )
    const regions = country?.regions
    if (!regions) {
      return []
    }
    return regions.map((region) => ({
      value: region.iso,
      label: region.name,
    }))
  }, [countries, currentCountry])

  return (
    <Styled.Container>
      <Styled.FieldRow>
        <TextInput
          autoComplete="address-line1"
          label="Street Address"
          name="street1"
          register={register}
          errors={errors}
          required
          disabled={disabled}
          defaultValue=""
          maxLength={ADDRESS_MAX_LENGTH}
          validations={addressMaxLengthRule}
        />
      </Styled.FieldRow>
      <Styled.FieldRow>
        <TextInput
          autoComplete="address-line2"
          label="Apartment, unit, suite, etc. (optional)"
          name="street2"
          register={register}
          errors={errors}
          disabled={disabled}
          defaultValue=""
          maxLength={ADDRESS_MAX_LENGTH}
          validations={addressMaxLengthRule}
        />
      </Styled.FieldRow>
      <Styled.FieldRow>
        <Styled.HalfColumn>
          <TextInput
            autoComplete="address-level2"
            label="City"
            name="city"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=""
          />
        </Styled.HalfColumn>
        <Styled.HalfColumn>
          <DropdownBasic
            // autoComplete="address-level1"
            label={fieldLabels.state}
            name="state"
            control={control}
            errors={errors}
            options={regionsList}
            defaultValue={undefined}
            required
            disabled={disabled}
          />
        </Styled.HalfColumn>
      </Styled.FieldRow>
      <Styled.FieldRow>
        <Styled.HalfColumn>
          <TextInput
            autoComplete="postal-code"
            label={fieldLabels.zipCode}
            name="zipCode"
            register={register}
            errors={errors}
            required
            disabled={disabled}
            defaultValue=""
          />
        </Styled.HalfColumn>
        <Styled.HalfColumn>
          <DropdownBasic
            // autoComplete="country"
            label="Country"
            name="country"
            control={control}
            errors={errors}
            options={countriesList}
            defaultValue={defaultCountryOption}
            required
            disabled={disabled}
          />
        </Styled.HalfColumn>
      </Styled.FieldRow>
    </Styled.Container>
  )
}
